import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { v4 as uuid } from "uuid";

import { ITestableComponentProps } from "@formant/core/model/ITestableComponentProps";

import { Field, ITextTypeFieldProps } from "./Field";
import styles from "./FloatingLabel.module.scss";

export interface ITextFieldProps
    extends
        ITestableComponentProps,
        ITextTypeFieldProps<HTMLInputElement>,
        ITestableComponentProps {
    id?: string;
    list?: string[];
    tooltip?: string;
    isEmail?: boolean;
    children?: React.ReactNode;
    autoComplete?: string;
}

@observer
export class TextField extends Component<ITextFieldProps> {
    @observable
    private isFocused: boolean = false;
    private listId = uuid();

    public render() {
        const {
            // <Field> props
            className,
            collapsed,
            errorMessage,
            icon,
            invalid,
            label,
            readOnly,
            small,

            // Component props
            id,
            disabled,
            value,
            onChange,
            onKeyDown,
            onClick,
            autoComplete,
            placeholder,
            list,
            autoFocus,
            spellCheck,
            children,
            tooltip,
            isEmail,
        } = this.props;

        const { isFocused } = this;

        return (
            <Field
                className={classnames(
                    className,
                    styles["floating-label-field"],
                    isFocused || (value && value.length > 0)
                        ? styles.focused
                        : null,
                    small ? styles.small : null,
                    icon ? styles["has-icon"] : null,
                )}
                collapsed={collapsed}
                errorMessage={errorMessage}
                filledOut={value !== undefined && value.length > 0}
                icon={icon}
                invalid={invalid}
                label={
                    isFocused ? (label ?? placeholder) : (placeholder ?? label)
                }
                readOnly={readOnly}
                small={small}
            >
                <input
                    id={id}
                    type={isEmail ? "email" : "text"}
                    {...{
                        disabled,
                        readOnly,
                        value,
                        onChange,
                        onFocus: this.onInputFocus,
                        onBlur: this.onInputBlur,
                        onKeyDown,
                        onClick,
                        autoComplete,
                        placeholder: undefined,
                        autoFocus,
                        spellCheck: !!spellCheck,
                        list: list ? this.listId : undefined,
                        onKeyDownCapture: this.props.onKeyDown,
                    }}
                    data-testid={this.props["data-testid"]}
                    data-tooltip={tooltip}
                    aria-invalid={invalid ? "true" : "false"}
                />
                {list && (
                    <datalist id={this.listId}>
                        {list.map(_ => (
                            <option key={_} value={_}>
                                {_}
                            </option>
                        ))}
                    </datalist>
                )}
                {children}
            </Field>
        );
    }

    private onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        const { onFocus } = this.props;
        this.isFocused = true;
        if (onFocus) {
            onFocus(event);
        }
    };

    private onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const { onBlur } = this.props;
        if (event.target.value.length === 0) {
            this.isFocused = false;
        }
        if (onBlur) {
            onBlur(event);
        }
    };
}
